import React, { useRef, useState, useEffect, useCallback, useContext } from 'react';
import { Box, IconButton, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CharacterEmbed2, { CharacterEmbed2Ref } from './CharacterEmbed2';
import { PureLightThemeCaaS } from 'src/theme/schemes/PureLightTheme';
import { mobile, isMobileWebBrowser } from 'src/utils/platformUtils';
import { WidgetContext } from 'src/contexts/WidgetContext';
import { shouldHideSignForStreamDrawer } from 'src/utils/streamDrawerSign';
import CreditsUi from 'src/components/CreditsUi/CreditsUi';
import FreeCreditsUi from 'src/components/CreditsUi/FreeCreditsUi';
import QuanAccountSpeedDial from 'src/components/SettingsLombard/QuanAccountSpeedDial';
import QuanSettingsSpeedDial from 'src/components/SettingsLombard/QuanSettingsSpeedDial';
import AudioSound from 'src/components/AudioPlayer/AudioSound';
import AccountSpeedDial from '../SettingsLombard/AccountSpeedDial';
//check if its mobile or mobile web browser 
const isMobile = isMobileWebBrowser() || mobile;

// --- BUBBLE CONFIGURATION ---
const HOMEPAGE_HEIGHT = isMobile ? '20vh' : '290px';
const HOMEPAGE_MIN_HEIGHT = isMobile ? 180 : 200;
const HOMEPAGE_MAX_HEIGHT = 300;

// Floating bubble: proporcional al viewport (más grande en desktop, bien en móvil)
const FLOATING_WIDTH = isMobile ? '50vw' : '55vw';
const FLOATING_MIN_WIDTH = isMobile ? 160 : 180;
const FLOATING_MAX_WIDTH = isMobile ? 260 : 270;
const FLOATING_HEIGHT = isMobile ? '16vh' : '20vh';
const FLOATING_MIN_HEIGHT = 100;
const FLOATING_MAX_HEIGHT = 220;

const BUBBLE_CONFIG = {
  // Homepage state (default position)
  homepage: {
    width: '100%',
    height: HOMEPAGE_HEIGHT,
    minHeight: HOMEPAGE_MIN_HEIGHT,
    maxHeight: HOMEPAGE_MAX_HEIGHT,
    borderRadius: '8px 8px 0 0',
  },
  // Floating state (when drawer is open) - proporcional al viewport
  floating: {
    width: FLOATING_WIDTH,
    height: FLOATING_HEIGHT,
    minWidth: FLOATING_MIN_WIDTH,
    maxWidth: FLOATING_MAX_WIDTH,
    minHeight: FLOATING_MIN_HEIGHT,
    maxHeight: FLOATING_MAX_HEIGHT,
    borderRadius: 1,
    bottom: 80,
    left: 16,
  },
  // Animation duration in ms
  transitionDuration: 300,
};
const CHARACTER_LOAD_TIMEOUT_MS = 15000; //15 seconds timeout to disabled quan when model never finishes loading on slow/unstable networks.

export interface CharacterBubble2Ref extends CharacterEmbed2Ref { }

interface CharacterBubble2Props {
  /** Whether the drawer is open (triggers floating mode) */
  isDrawerOpen: boolean;
  /** Background color for the character viewer */
  backgroundColor?: string;
  /** Callback when character loads */
  onLoad?: () => void;
  onReadyStateChange?: (isReady: boolean) => void;
  /** Callback when character has an error */
  onError?: (error: Error) => void;
  /** Callback when character loading exceeds timeout */
  onLoadTimeout?: () => void;
  /** Selected button */
  selectedButton?: string;
  /** Allow thought sign while drawer is open (trade analyze / draft refresh in-drawer streams only). */
  allowSignWhileDrawerOpen?: boolean;
  /** Hide sign when the trade response is already visible (so user reads/listens with lipsync). */
  hideSignForTradeResult?: boolean;
  shouldPlayAudio: boolean;
  authId: string;
  setIsSliderOpen: (isOpen: boolean) => void;
  characterRef: React.RefObject<CharacterBubble2Ref>;
  thoughtsCommon: string;
  isSliderOpen: boolean;
  isMobile: boolean;
  isChangingPlatform: boolean;
  handlePartnerSelectAndCloseDrawer: () => void;
  isAiDialogOpen: boolean;
  setIsAiDialogOpen: (isOpen: boolean) => void;
  handleConfirmAiSettings: (nextSettings: Record<string, boolean>) => void | Promise<void>;
  aiSettings: any;
  isUpdatingAiSettings: boolean;
  isPaymentOpen: boolean;
  setIsPaymentOpen: (isOpen: boolean) => void;
  setInitialPaymentView: (initialPaymentView: string) => void;
  initialPaymentView: string;
  enableCreditValidation: boolean;
  userSubscription: string;
  isCreditsAnimating: boolean;
  creditsBoxRef: React.RefObject<HTMLDivElement>;
}

const CharacterBubble2 = React.forwardRef<CharacterBubble2Ref, CharacterBubble2Props>(
  (
    {
      isDrawerOpen,
      backgroundColor = '#000000',
      onLoad,
      onReadyStateChange,
      onError,
      onLoadTimeout,
      selectedButton,
      allowSignWhileDrawerOpen = false,
      hideSignForTradeResult = false,
      shouldPlayAudio, authId, setIsSliderOpen, characterRef, thoughtsCommon, isSliderOpen, isMobile, isChangingPlatform, handlePartnerSelectAndCloseDrawer, isAiDialogOpen, setIsAiDialogOpen, handleConfirmAiSettings, aiSettings, isUpdatingAiSettings, isPaymentOpen, setIsPaymentOpen, setInitialPaymentView, initialPaymentView, userSubscription, enableCreditValidation, isCreditsAnimating, creditsBoxRef
    },
    ref
  ) => {
    // State to track if user manually closed the bubble
    const [isBubbleClosed, setIsBubbleClosed] = useState(false);
    // State for character loading (model + animation ready)
    const [isCharacterLoading, setIsCharacterLoading] = useState(true);

    useEffect(() => {
      onReadyStateChange?.(!isCharacterLoading);
    }, [isCharacterLoading, onReadyStateChange]);
    const { credits, freeCredits, isLoggedInAuth0ExperienceGlobal } = useContext(WidgetContext);

    // State for drag position (only used in floating mode)
    const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);

    // Refs for drag handling
    const bubbleRef = useRef<HTMLDivElement>(null);
    const dragStartRef = useRef({ x: 0, y: 0 });
    const initialPosRef = useRef({ x: 0, y: 0 });

    // Drawer sections with AI bubble
    const DRAWER_SECTIONS_WITH_AI_BUBBLE: string[] = [
      "waiver",
      "start",
      "rundown",
      "trades",
      "DraftAdvice",
    ];

    // Check if the selected button is an AI section to show the bubble
    const isAiSectionSelected = selectedButton != null && DRAWER_SECTIONS_WITH_AI_BUBBLE.includes(selectedButton);

    // Determine if we should show floating mode
    // const isFloating = isDrawerOpen && !isBubbleClosed && isAiSectionSelected;
    const isFloating = false; //ON PURPOSE TO HIDE FLOATING BUBBLE ALWAYS

    // Reset bubble closed state when drawer closes
    useEffect(() => {
      if (!isDrawerOpen) {
        setIsBubbleClosed(false);
        setDragPosition({ x: 0, y: 0 }); // Reset drag position
      }
    }, [isDrawerOpen]);

    // Handle close button click
    const handleCloseBubble = useCallback(() => {
      setIsBubbleClosed(true);
      setDragPosition({ x: 0, y: 0 }); // Reset drag position
    }, []);

    // Character load/error: hide loader and notify parent
    const handleCharacterLoad = useCallback(() => {
      setIsCharacterLoading(false);
      onLoad?.();
    }, [onLoad]);

    const handleCharacterError = useCallback(
      (err: Error) => {
        setIsCharacterLoading(false);
        onError?.(err);
      },
      [onError]
    );

    // Show loader overlay while CharacterEmbed2 rebuilds after background resume.
    const handleBeforeRebuild = useCallback(() => {
      setIsCharacterLoading(true);
    }, []);

    // Fallback when slow networks keep the model loading indefinitely.
    useEffect(() => {
      if (!isCharacterLoading) return;

      const timeoutId = window.setTimeout(() => {
        onLoadTimeout?.();
        console.log("Character load timeout after", CHARACTER_LOAD_TIMEOUT_MS, "ms. So disabled Quan");
      }, CHARACTER_LOAD_TIMEOUT_MS);

      return () => {
        window.clearTimeout(timeoutId);
      };
    }, [isCharacterLoading, onLoadTimeout]);

    // --- DRAG HANDLERS ---
    const handleDragStart = useCallback((clientX: number, clientY: number) => {
      if (!isFloating) return;

      setIsDragging(true);
      dragStartRef.current = { x: clientX, y: clientY };
      initialPosRef.current = { ...dragPosition };
    }, [isFloating, dragPosition]);

    const handleDragMove = useCallback((clientX: number, clientY: number) => {
      if (!isDragging || !isFloating) return;

      const deltaX = clientX - dragStartRef.current.x;
      const deltaY = clientY - dragStartRef.current.y;

      // Calculate new position
      let newX = initialPosRef.current.x + deltaX;
      let newY = initialPosRef.current.y - deltaY; // Invert Y because bottom-based

      // Get viewport bounds and actual bubble size (floating size can be vw/vh)
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const rect = bubbleRef.current?.getBoundingClientRect();
      const bubbleWidth = rect?.width ?? FLOATING_MIN_WIDTH;
      const bubbleHeight = rect?.height ?? FLOATING_MIN_HEIGHT;

      // Constrain to viewport (with some padding)
      const padding = 8;
      const maxX = viewportWidth - bubbleWidth - BUBBLE_CONFIG.floating.left - padding;
      const maxY = viewportHeight - bubbleHeight - BUBBLE_CONFIG.floating.bottom - padding;

      newX = Math.max(-BUBBLE_CONFIG.floating.left + padding, Math.min(maxX, newX));
      newY = Math.max(-BUBBLE_CONFIG.floating.bottom + padding, Math.min(maxY, newY));

      setDragPosition({ x: newX, y: newY });
    }, [isDragging, isFloating]);

    const handleDragEnd = useCallback(() => {
      setIsDragging(false);
    }, []);

    // Mouse event handlers
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
      e.preventDefault();
      handleDragStart(e.clientX, e.clientY);
    }, [handleDragStart]);

    const handleMouseMove = useCallback((e: MouseEvent) => {
      handleDragMove(e.clientX, e.clientY);
    }, [handleDragMove]);

    const handleMouseUp = useCallback(() => {
      handleDragEnd();
    }, [handleDragEnd]);

    // Touch event handlers
    const handleTouchStart = useCallback((e: React.TouchEvent) => {
      if (e.touches.length === 1) {
        handleDragStart(e.touches[0].clientX, e.touches[0].clientY);
      }
    }, [handleDragStart]);

    const handleTouchMove = useCallback((e: TouchEvent) => {
      if (e.touches.length === 1) {
        handleDragMove(e.touches[0].clientX, e.touches[0].clientY);
      }
    }, [handleDragMove]);

    const handleTouchEnd = useCallback(() => {
      handleDragEnd();
    }, [handleDragEnd]);

    // Add/remove global event listeners for drag
    useEffect(() => {
      if (isDragging) {
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        window.addEventListener('touchmove', handleTouchMove, { passive: false });
        window.addEventListener('touchend', handleTouchEnd);
      }

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
      };
    }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

    // Styles for the bubble container
    const bubbleStyles = isFloating
      ? {
        position: 'fixed' as const,
        bottom: BUBBLE_CONFIG.floating.bottom + dragPosition.y,
        left: BUBBLE_CONFIG.floating.left + dragPosition.x,
        width: BUBBLE_CONFIG.floating.width,
        height: BUBBLE_CONFIG.floating.height,
        minWidth: BUBBLE_CONFIG.floating.minWidth,
        maxWidth: BUBBLE_CONFIG.floating.maxWidth,
        minHeight: BUBBLE_CONFIG.floating.minHeight,
        maxHeight: BUBBLE_CONFIG.floating.maxHeight,
        borderRadius: BUBBLE_CONFIG.floating.borderRadius,
        zIndex: 2000, // Above most MUI components
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
        cursor: isDragging ? 'grabbing' : 'grab',
        transition: isDragging ? 'none' : `all ${BUBBLE_CONFIG.transitionDuration}ms ease-in-out`,
        overflow: 'hidden',
      }
      : {
        position: 'relative' as const,
        width: BUBBLE_CONFIG.homepage.width,
        height: BUBBLE_CONFIG.homepage.height,
        minHeight: BUBBLE_CONFIG.homepage.minHeight,
        maxHeight: BUBBLE_CONFIG.homepage.maxHeight,
        borderRadius: BUBBLE_CONFIG.homepage.borderRadius,
        transition: `all ${BUBBLE_CONFIG.transitionDuration}ms ease-in-out`,
        overflow: 'hidden',
      };

    // Hide sign only for stream section drawers (waiver/start/rundown/draft); trade/chatbot/etc. keep Quan visible.
    const hideSignForStreamDrawer =
      isDrawerOpen && shouldHideSignForStreamDrawer(selectedButton);
    const showSign =
      !!thoughtsCommon &&
      !isChangingPlatform &&
      (!hideSignForStreamDrawer || allowSignWhileDrawerOpen) &&
      !hideSignForTradeResult;

    return (
      <Box
        ref={bubbleRef}
        sx={bubbleStyles}
        onMouseDown={isFloating ? handleMouseDown : undefined}
        onTouchStart={isFloating ? handleTouchStart : undefined}
      >
        {/* Close button (only visible in floating mode) */}
        {isFloating && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleCloseBubble();
            }}
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              zIndex: 10,
              backgroundColor: 'black',
              color: 'white',
              padding: '4px',
              '&:hover': {
                backgroundColor: 'black',
              },
              borderRadius: 2
            }}
            size="small"
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}

        {/* Audio icon in floating mode: bottom-left, moves with bubble; stop propagation so click doesn't start drag */}
        {isFloating && (
          <Box
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            sx={{
              position: 'absolute',
              bottom: 4,
              left: 4,
              zIndex: 10,
              pointerEvents: 'auto',
            }}
          >
            <AudioSound
              shouldPlayAudio={shouldPlayAudio}
              authId={authId}
              onSliderVisibilityChange={setIsSliderOpen}
              characterRef={characterRef}
              isFloating={isFloating}
              isCharacterReady={!isCharacterLoading}
            />
          </Box>
        )}

        {/* Loader overlay: fully opaque so character is not visible until ready */}
        {isCharacterLoading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: backgroundColor,
              zIndex: 5,
            }}
          >
            <CircularProgress size={40} sx={{ color: `${PureLightThemeCaaS.colors.alpha.orange[100]}` }} />
          </Box>
        )}

        {/* Character Embed - hidden until onLoad so user never sees bad positioning */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            visibility: isCharacterLoading ? 'hidden' : 'visible',
            transition: 'visibility 0.2s ease-out',
          }}
        >
          <CharacterEmbed2
            ref={ref}
            height="100%"
            backgroundColor={backgroundColor}
            onLoad={handleCharacterLoad}
            onError={handleCharacterError}
            onBeforeRebuild={handleBeforeRebuild}
            showSign={showSign}
            signText={thoughtsCommon}
          />
        </Box>

        {/* Homepage-only overlay: 4 corner UI (Quan view, not floating) */}
        {!isFloating && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              display: 'flex',
              flexDirection: 'row',
              zIndex: 2,
            }}
          >
            {/* Thoughts dialog: black box left of character, only when thoughtsCommon exists; does not cover corner icons */}
            {/* {!!thoughtsCommon && !isDrawerOpen && (!isSliderOpen || !isMobile) && !isChangingPlatform && !!selectedTeamInfo && (
              <Box
                sx={{
                  flex: '0 0 50%',
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 2,
                  ml: isMobile ? 0 : 4,
                  pointerEvents: 'auto',
                }}
              >
                <Fade in timeout={1000}>
                  <Box
                    sx={{
                      pointerEvents: 'auto',
                      zIndex: 1,
                      backgroundColor: 'black',
                      borderRadius: 1,
                      border: `1px solid ${PureLightThemeCaaS.colors.alpha.gray[100]}`,
                      p: 0.5,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: isMobile ? '100%' : '60%',
                      height: isMobile ? '60%' : '45%',
                      margin: 'auto 0',
                      // minHeight: 0,
                      overflow: 'hidden',
                    }}
                  >
                    <ThoughtItemCommon text={thoughtsCommon} allowWrap />
                  </Box>
                </Fade>
              </Box>
            )} */}
            {/* Sign mesh + text live inside CharacterEmbed2 (same scene as newQuan + room GLB). */}
            {/* Top right: account speed dial */}
            {isLoggedInAuth0ExperienceGlobal ? (
              <Box sx={{ position: 'absolute', top: 8, right: 8, pointerEvents: 'auto', zIndex: 10 }}>
                <QuanAccountSpeedDial
                  onClose={handlePartnerSelectAndCloseDrawer}
                  isAiDialogOpen={isAiDialogOpen}
                  onOpenAiSettings={() => setIsAiDialogOpen(true)}
                  onCloseAiSettings={() => setIsAiDialogOpen(false)}
                  onConfirmAiSettings={handleConfirmAiSettings}
                  aiSettings={aiSettings}
                  isUpdatingAiSettings={isUpdatingAiSettings}
                  isPaymentOpen={isPaymentOpen}
                  onPaymentOpenChange={(isOpen) => {
                    if (isOpen) setInitialPaymentView('menu');
                    setIsPaymentOpen(isOpen);
                  }}
                  initialPaymentView={initialPaymentView}
                  credits={credits}
                  enableCreditValidation={enableCreditValidation}
                  userSubscription={userSubscription}
                />
              </Box>
            ) : (
              <Box sx={{ position: 'absolute', top: 8, right: 8, pointerEvents: 'auto', zIndex: 10 }}>
                <AccountSpeedDial />
              </Box>
            )}
            {/* Bottom right: credits */}
            <Box sx={{ position: 'absolute', bottom: 8, right: 8, pointerEvents: 'auto', zIndex: 10 }}>
              {isLoggedInAuth0ExperienceGlobal && userSubscription !== 'free' ? (
                <CreditsUi credits={credits} isCreditsAnimating={isCreditsAnimating} creditsBoxRef={creditsBoxRef} />
              ) : (
                <FreeCreditsUi freeCredits={freeCredits} isCreditsAnimating={isCreditsAnimating} creditsBoxRef={creditsBoxRef} />
              )}
            </Box>
            {/* Bottom left: audio */}
            <Box sx={{ position: 'absolute', bottom: 8, left: 8, pointerEvents: 'auto', zIndex: 10 }}>
              <AudioSound
                shouldPlayAudio={shouldPlayAudio}
                authId={authId}
                onSliderVisibilityChange={setIsSliderOpen}
                characterRef={characterRef}
                isCharacterReady={!isCharacterLoading}
              />
            </Box>
            {/* Top left: settings speed dial */}
            <Box sx={{ position: 'absolute', top: 8, left: 8, pointerEvents: 'auto', zIndex: 10 }}>
              <QuanSettingsSpeedDial
                onClose={handlePartnerSelectAndCloseDrawer}
                isAiDialogOpen={isAiDialogOpen}
                onOpenAiSettings={() => setIsAiDialogOpen(true)}
                onCloseAiSettings={() => setIsAiDialogOpen(false)}
                onConfirmAiSettings={handleConfirmAiSettings}
                aiSettings={aiSettings}
                isUpdatingAiSettings={isUpdatingAiSettings}
                isPaymentOpen={isPaymentOpen}
                onPaymentOpenChange={(isOpen) => {
                  if (isOpen) setInitialPaymentView('menu');
                  setIsPaymentOpen(isOpen);
                }}
                initialPaymentView={initialPaymentView}
                credits={credits}
                enableCreditValidation={enableCreditValidation}
              />
            </Box>
          </Box>
        )}
      </Box>
    );
  }
);

CharacterBubble2.displayName = 'CharacterBubble2';

export default CharacterBubble2;
